/**
 * NotificationsPopOver — Panel de notificaciones WhatsApp (refactor 0282)
 *
 * Lógica extraída a hooks siguiendo el patrón Kanban (0258):
 *   - useNotificationsData: fetch API + mutaciones de estado
 *   - useNotificationsSocket: stream de eventos Socket.IO
 *
 * Fixes aplicados:
 *   - Avatar: campo correcto profilePicUrl (antes .avatar → siempre undefined)
 *   - Render: solo muestra notificaciones sin leer (!isRead); leídas desaparecen
 *   - Badge de mensajes: muestra unreadMessages real (antes hardcodeado en 1)
 *   - PENDING sync: aceptar/finalizar marca como leído y saca de la lista
 *   - API: usa axios (api) en lugar de fetch manual
 */
import React, { useState, useRef, useContext, useEffect, useCallback } from "react";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import CheckIcon from "@material-ui/icons/Check";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import Badge from "@material-ui/core/Badge";
import Popover from "@material-ui/core/Popover";
import Typography from "@material-ui/core/Typography";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import Tooltip from "@material-ui/core/Tooltip";
import { useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";

import { useHistory } from "react-router-dom";
import notifySound from "../../assets/chat_notify.mp3";
import { AuthContext } from "../../context/Auth/AuthContext";
import { getProfilePicUrl } from "../../services/api";
import Avatar from "@material-ui/core/Avatar";
import { getInitials } from "../../helpers/getInitials";
import { generateColor } from "../../helpers/colorGenerator";
import { getLogos } from "../../services/settings.api";
import { acceptTicket as acceptTicketApi, closeTicket as closeTicketApi } from "../../services/tickets.api";
import { useTimezone } from "../../hooks/useTimezone";

import useNotificationsData from "./hooks/useNotificationsData";
import ChannelIcon from "../ChannelIcon";
import { getWhatsAppProviderLabel } from "../../constants/whatsappProviders";
import useNotificationsSocket from "./hooks/useNotificationsSocket";

// ─── helpers de UI ─────────────────────────────────────────────────────────────

/**
 * ¿Esta URL es una foto de verdad?
 *
 * Baileys **no captura el avatar de los grupos**: guarda el marcador
 * `nopicture.png`, que además ni siquiera existe en el servidor —da 404—. Los 5
 * grupos de la empresa 6 lo tienen, y 124 de sus 130 personas no tienen ninguna.
 *
 * Es EXACTAMENTE el criterio que ya aplica `TicketListItemCustom`, copiado a
 * propósito para que el aviso y la bandeja pinten lo mismo ante el mismo dato.
 */
function esFotoReal(url) {
  return !!url && url !== "" && !url.includes("nopicture.png");
}

/**
 * La franja de estado usa los colores SEMÁNTICOS del tema, no unos propios.
 * Así un tema que redefina «advertencia» o «éxito» arrastra también esta franja.
 */
function getStatusColor(status, paleta) {
  if (status === "open") return paleta.aceptar;
  if (status === "pending") return paleta.pendiente;
  if (status === "closed") return paleta.finalizar;
  return paleta.borde;
}

function getTooltipText(status) {
  if (status === "pending") return "⚠️ Ticket pendiente — Debes aceptarlo o finalizarlo primero";
  if (status === "closed") return "📋 Click para ver el ticket cerrado";
  return "✅ Click para abrir el ticket";
}

// ─── componente ───────────────────────────────────────────────────────────────

const NotificationsPopOver = ({ volume: volumeProp, customStyle }) => {
  const theme = useTheme();

  /**
   * Paleta derivada del tema.
   *
   * Este componente tenía 45 colores escritos a mano —el peor de la barra
   * superior, frente a los 2 de `ChatPopover`—, incluido un degradado violeta
   * `#667eea → #764ba2` que no pertenece a ningún tema del sistema. En modo
   * oscuro el resultado eran dos mundos pegados: cabecera morada y filas
   * blancas.
   *
   * Todo el color sale ahora de aquí, así que el popover cambia con el tema sin
   * tocar una línea, y un tema nuevo lo hereda gratis.
   */
  const paleta = {
    fondo: theme.palette.background.paper,
    fondoAlterno: theme.palette.action.hover,
    texto: theme.palette.text.primary,
    textoSuave: theme.palette.text.secondary,
    borde: theme.palette.divider,
    acento: theme.palette.primary.main,
    sobreAcento: theme.palette.primary.contrastText,
    pendiente: theme.palette.warning.main,
    aceptar: theme.palette.success.main,
    finalizar: theme.palette.error.main
  };
  const isXs = useMediaQuery(theme.breakpoints.down("xs"));
  const { user } = useContext(AuthContext);
  const { getTimeAgo } = useTimezone();
  const history = useHistory();

  // ── Datos y mutaciones de estado ──────────────────────────────────────────
  const {
    notifications,
    fetchNotifications,
    markAsRead,
    applySocketMessage,
    applyTicketEvent,
    applyContactDelete,
  } = useNotificationsData({ user });

  // Solo se renderizan notificaciones no leídas
  const unread = notifications.filter((n) => !n.isRead);
  const notificationCount = unread.length;

  // Tick cada 60s para refrescar los tiempos relativos sin depender de socket events
  const [, setTimeTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTimeTick(t => t + 1), 60_000);
    return () => clearInterval(id);
  }, []);

  // ── Sonido ────────────────────────────────────────────────────────────────
  const volume =
    volumeProp !== undefined ? volumeProp : Number(localStorage.getItem("volume")) || 0.5;
  const audioRef = useRef(null);
  const volumeRef = useRef(volume);

  useEffect(() => {
    if (!audioRef.current) {
      audioRef.current = new Audio(notifySound);
      audioRef.current.preload = "auto";
      audioRef.current.volume = volume;
    }
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    volumeRef.current = volume;
    if (audioRef.current) audioRef.current.volume = volume;
  }, [volume]);

  // Desbloqueo de autoplay: navegador exige gesto de usuario antes de Audio.play()
  useEffect(() => {
    const unlock = () => {
      const a = audioRef.current;
      if (!a) return;
      const v = volumeRef.current;
      a.muted = true;
      a.play()
        .then(() => { a.pause(); a.currentTime = 0; a.muted = false; a.volume = v; })
        .catch(() => { a.muted = false; a.volume = v; });
      window.removeEventListener("pointerdown", unlock, true);
      window.removeEventListener("keydown", unlock, true);
    };
    window.addEventListener("pointerdown", unlock, true);
    window.addEventListener("keydown", unlock, true);
    return () => {
      window.removeEventListener("pointerdown", unlock, true);
      window.removeEventListener("keydown", unlock, true);
    };
  }, []);

  const playSound = useCallback(() => {
    const a = audioRef.current;
    if (!a) return;
    a.muted = false;
    a.volume = volumeRef.current;
    a.currentTime = 0;
    a.play().catch((err) => {
      if (err?.name !== "NotAllowedError") console.error("Error reproduciendo sonido:", err);
    });
  }, []);

  // ── Favicon dinámico para notificaciones nativas ───────────────────────────
  const [faviconUrl, setFaviconUrl] = useState("/favicon.ico");
  useEffect(() => {
    getLogos()
      .then(({ data }) => {
        const fav = data?.logos?.find((l) => l.type === "favicon");
        if (fav?.url) setFaviconUrl(fav.url);
      })
      .catch(() => {});
  }, []);

  // ── Carga inicial ──────────────────────────────────────────────────────────
  useEffect(() => {
    fetchNotifications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Callback para el hook de socket: sonido + notificación nativa ──────────
  const handleNewMessage = useCallback(
    (data) => {
      playSound();
      if ("Notification" in window && Notification.permission === "granted") {
        const label = data.contact?.name || "Contacto";
        new Notification(`Nuevo mensaje de ${label}`, {
          body: data.message?.body || "Actualización",
          icon: faviconUrl,
          tag: `ticket-${data.ticket?.id}`,
          requireInteraction: false,
        });
      }
    },
    [playSound, faviconUrl]
  );

  /**
   * Transferencia entrante: el MISMO sonido que un mensaje nuevo.
   *
   * WHY un solo tono y no dos: en ambos casos la reacción del agente es la
   * misma —hay trabajo en PENDIENTE, voy a mirar—. Dos timbres parecidos en una
   * jornada de 8 horas confunden más de lo que informan. La distinción va donde
   * sí se lee: en el texto del aviso.
   */
  const handleIncomingTransfer = useCallback(
    (data) => {
      playSound();
      if ("Notification" in window && Notification.permission === "granted") {
        const depto = data.traspaso?.departamento;
        new Notification(
          depto ? `Ticket transferido a ${depto}` : "Ticket transferido",
          {
            body: data.ticket?.contact?.name || "Nuevo ticket en tu bandeja",
            icon: faviconUrl,
            tag: `transfer-${data.ticket?.id || data.ticketId}`,
            requireInteraction: false,
          }
        );
      }
      fetchNotifications();
    },
    [playSound, faviconUrl, fetchNotifications]
  );

  /**
   * Ticket nacido de un Lead Web (doc 0417, Punto 4) — el MISMO sonido que un
   * mensaje nuevo o una transferencia (un solo tono para las tres cosas, a
   * propósito: la reacción del agente es la misma, "hay trabajo nuevo, voy a
   * mirar"), pero un título propio: nunca dice "mensaje" porque no llegó
   * ningún WhatsApp — llegó una solicitud de formulario que se convirtió en
   * ticket. Decirlo distinto es lo honesto, no un detalle de estilo.
   */
  const handleNewWebLead = useCallback(
    (data) => {
      playSound();
      if ("Notification" in window && Notification.permission === "granted") {
        const nombre = data.ticket?.contact?.name;
        new Notification("Nueva solicitud web", {
          body: nombre ? `${nombre} — se convirtió en ticket` : "Nuevo lead convertido en ticket",
          icon: faviconUrl,
          tag: `weblead-${data.ticket?.id || data.ticketId}`,
          requireInteraction: false,
        });
      }
      fetchNotifications();
    },
    [playSound, faviconUrl, fetchNotifications]
  );

  /**
   * Lead en modo seguro (0417, Punto 4 — mejora post-cierre): nunca dice
   * "ticket" ni "mensaje" porque no se creó ninguno de los dos — solo se
   * vinculó un Contacto y está esperando que el cliente escriba, o que el
   * call center lo llame manualmente (el escenario real que reportó el
   * propietario: "atención al cliente nunca se entera que llegó nuevo
   * lead"). No entra a `fetchNotifications()` — esa lista es 100% de
   * tickets y este lead no tiene uno; el aviso es solo sonido +
   * notificación nativa, honesto sobre lo que puede ofrecer hoy.
   */
  const handleModoSeguroLead = useCallback(
    (data) => {
      playSound();
      if ("Notification" in window && Notification.permission === "granted") {
        new Notification("Nuevo lead — esperando contacto", {
          body: data.fullName
            ? `${data.fullName} — vinculado a Contacto, sin ticket. Revisa Agenda › Solicitudes web.`
            : "Nuevo lead en modo seguro. Revisa Agenda › Solicitudes web.",
          icon: faviconUrl,
          tag: `weblead-linked-${data.leadId}`,
          requireInteraction: false,
        });
      }
    },
    [playSound, faviconUrl]
  );

  // ── Stream de socket ───────────────────────────────────────────────────────
  useNotificationsSocket({
    user,
    applySocketMessage,
    applyTicketEvent,
    applyContactDelete,
    onNewMessage: handleNewMessage,
    onIncomingTransfer: handleIncomingTransfer,
    onNewWebLead: handleNewWebLead,
    onModoSeguroLead: handleModoSeguroLead,
    fetchNotifications,
  });

  // ── Popover state ──────────────────────────────────────────────────────────
  const [anchorEl, setAnchorEl] = useState(null);

  const handleClick = (e) => {
    e.stopPropagation();
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission().then(() => {});
    }
    setAnchorEl(e.currentTarget);
  };

  const handleClose = (e) => {
    if (e) e.stopPropagation();
    setAnchorEl(null);
  };

  // ── Acciones de ticket ─────────────────────────────────────────────────────
  const navigateToTicket = (notification) => {
    const uuid = notification.metadata?.ticketUuid;
    const path = uuid ? `/tickets/${uuid}` : `/tickets/${notification.ticketId}`;
    handleClose();
    history.push(path);
  };

  const goToTicket = async (notification) => {
    if (notification.ticketStatus === "pending") return;
    await markAsRead(notification.ticketId);
    navigateToTicket(notification);
  };

  const acceptTicket = async (notification) => {
    try {
      await acceptTicketApi(notification.ticketId);
      await markAsRead(notification.ticketId);
      navigateToTicket(notification);
    } catch (err) {
      console.error("Error aceptando ticket:", err);
    }
  };

  const finalizeTicket = async (notification) => {
    try {
      await closeTicketApi(notification.ticketId);
      await markAsRead(notification.ticketId);
    } catch (err) {
      console.error("Error finalizando ticket:", err);
    }
  };

  // ─── render ────────────────────────────────────────────────────────────────
  return (
    <div>
      <IconButton
        color="inherit"
        onClick={handleClick}
        onMouseDown={(e) => e.stopPropagation()}
        size="small"
        style={customStyle || {}}
        onMouseEnter={(e) => {
          if (customStyle) {
            e.currentTarget.style.color = theme.palette.primary.main;
            e.currentTarget.style.backgroundColor = theme.palette.action.hover;
          }
        }}
        onMouseLeave={(e) => {
          if (customStyle) {
            e.currentTarget.style.color = theme.palette.text.secondary;
            e.currentTarget.style.backgroundColor = "transparent";
          }
        }}
      >
        <Badge
          badgeContent={notificationCount}
          color="secondary"
          max={99}
          invisible={notificationCount === 0}
        >
          {customStyle ? (
            <WhatsAppIcon fontSize="small" style={{ color: theme.palette.text.primary }} />
          ) : (
            <WhatsAppIcon style={{ color: "white" }} />
          )}
        </Badge>
      </IconButton>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        marginThreshold={isXs ? 8 : 16}
        anchorOrigin={{ vertical: "bottom", horizontal: isXs ? "center" : "right" }}
        transformOrigin={{ vertical: "top", horizontal: isXs ? "center" : "right" }}
        style={{ zIndex: 1300 }}
        PaperProps={{
          style: {
            width: isXs ? "calc(100vw - 16px)" : "min(400px, calc(100vw - 24px))",
            maxWidth: "100%",
            maxHeight: "min(85vh, 560px)",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        <div
          style={{
            width: "100%",
            boxSizing: "border-box",
            flex: 1,
            minHeight: 0,
            maxHeight: "min(85vh, 560px)",
            overflowY: "auto",
            overflowX: "hidden",
            WebkitOverflowScrolling: "touch",
            backgroundColor: paleta.fondo,
          }}
        >
          {/* Cabecera — el acento del tema marca, no un degradado propio */}
          <div
            style={{
              padding: "12px 16px",
              borderBottom: `1px solid ${paleta.borde}`,
              backgroundColor: paleta.fondo,
              position: "sticky",
              top: 0,
              zIndex: 1,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 8,
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <Typography
                component="div"
                style={{
                  fontWeight: 600,
                  color: paleta.texto,
                  fontSize: "15px",
                  lineHeight: 1.3,
                }}
              >
                Notificaciones
              </Typography>
              <Typography
                component="div"
                style={{ color: paleta.textoSuave, fontSize: "12px", marginTop: 1 }}
              >
                {notificationCount > 0
                  ? `${notificationCount} mensaje${notificationCount !== 1 ? "s" : ""} sin leer`
                  : "No hay mensajes nuevos"}
              </Typography>
            </div>
            <IconButton
              onClick={(e) => { e.stopPropagation(); handleClose(e); }}
              size="small"
              style={{ color: paleta.textoSuave, padding: 4 }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </div>

          {/* Lista / estado vacío */}
          {unread.length === 0 ? (
            <div style={{ padding: "36px 24px", textAlign: "center" }}>
              <div style={{ fontSize: 34, marginBottom: 10, opacity: 0.5 }}>
                <span role="img" aria-label="empty">📭</span>
              </div>
              <Typography
                component="div"
                style={{ color: paleta.texto, fontWeight: 600, fontSize: "14px" }}
              >
                No hay notificaciones nuevas
              </Typography>
              <Typography
                component="div"
                style={{ color: paleta.textoSuave, fontSize: "12px", marginTop: 4 }}
              >
                Cuando recibas mensajes de WhatsApp aparecerán aquí
              </Typography>
            </div>
          ) : (
            <List style={{ padding: 0 }}>
              {unread.map((notification) => (
                <Tooltip
                  key={notification.id}
                  title={getTooltipText(notification.ticketStatus)}
                  placement="left"
                >
                  <ListItem
                    button
                    onClick={() => goToTicket(notification)}
                    style={{
                      backgroundColor: paleta.fondo,
                      borderBottom: `1px solid ${paleta.borde}`,
                      cursor: notification.ticketStatus === "pending" ? "not-allowed" : "pointer",
                      // Densidad: era una tarjeta con sombra dentro de una lista.
                      // Una lista no necesita tarjetas; la franja de estado basta.
                      padding: "10px 14px",
                      margin: 0,
                      position: "relative",
                      borderLeft: `3px solid ${getStatusColor(notification.ticketStatus, paleta)}`,
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = paleta.fondoAlterno;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = paleta.fondo;
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "flex-start", gap: 10, width: "100%" }}>
                      {/* Avatar con contador real de mensajes */}
                      <div style={{ position: "relative", flexShrink: 0 }}>
                        <Avatar
                          src={
                            esFotoReal(notification.contactAvatar)
                              ? getProfilePicUrl(notification.contactAvatar)
                              : null
                          }
                          alt={notification.contactName}
                          style={{
                            width: 36,
                            height: 36,
                            fontSize: "13px",
                            fontWeight: 600,
                            backgroundColor: generateColor(notification.contactNumber),
                            color: "#fff",
                          }}
                        >
                          {getInitials(notification.contactName || "")}
                        </Avatar>
                        {/* Badge con conteo real de mensajes sin leer */}
                        <div
                          style={{
                            position: "absolute",
                            bottom: -2,
                            right: -2,
                            minWidth: 18,
                            height: 18,
                            padding: "0 4px",
                            borderRadius: 9,
                            backgroundColor: paleta.acento,
                            color: paleta.sobreAcento,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "10px",
                            fontWeight: 700,
                            border: `2px solid ${paleta.fondo}`,
                          }}
                        >
                          {notification.unreadMessages > 99 ? "99+" : notification.unreadMessages || 1}
                        </div>
                      </div>

                      {/* Contenido principal */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        {/* Fila superior: Nombre y tiempo */}
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            marginBottom: 2,
                          }}
                        >
                          <Typography
                            variant="subtitle2"
                            style={{
                              fontWeight: 600,
                              fontSize: "13px",
                              color: paleta.texto,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {notification.contactName || "Contacto"}
                          </Typography>
                          <Typography
                            variant="caption"
                            style={{ fontSize: "11px", color: paleta.textoSuave, marginLeft: 8, flexShrink: 0 }}
                          >
                            {getTimeAgo(notification.timestamp)?.labelText ?? ""}
                          </Typography>
                        </div>

                        {/* Mensaje */}
                        <Typography
                          variant="body2"
                          style={{
                            marginBottom: 4,
                            fontSize: "12px",
                            color: paleta.textoSuave,
                            lineHeight: 1.35,
                          }}
                        >
                          {notification.message.length > 50
                            ? `${notification.message.substring(0, 50)}...`
                            : notification.message}
                        </Typography>

                        {/* Fila inferior: ticket info + acciones */}
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                          }}
                        >
                          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                            {/* El canal decide cómo se responde: correo, Telegram y WhatsApp no se atienden igual. */}
                            {notification.provider && (
                              <>
                                <ChannelIcon provider={notification.provider} size={12} />
                                <Typography variant="caption" style={{ fontSize: "10px", color: paleta.textoSuave, fontWeight: 600 }}>
                                  {getWhatsAppProviderLabel(notification.provider)}
                                </Typography>
                                <span style={{ color: paleta.textoSuave, fontSize: "10px", opacity: 0.5 }}>•</span>
                              </>
                            )}
                            <Typography variant="caption" style={{ fontSize: "10px", color: paleta.textoSuave, opacity: 0.85 }}>
                              Ticket #{notification.ticketId}
                            </Typography>
                            {notification.queueName && (
                              <>
                                <span style={{ color: paleta.textoSuave, fontSize: "10px", opacity: 0.5 }}>•</span>
                                <Typography variant="caption" style={{ fontSize: "10px", color: paleta.textoSuave, opacity: 0.85 }}>
                                  {notification.queueName}
                                </Typography>
                              </>
                            )}
                          </div>

                          {/* Badge PENDING + acciones */}
                          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            {notification.ticketStatus === "pending" && (
                              <div
                                style={{
                                  backgroundColor: paleta.pendiente,
                                  color: paleta.sobreAcento,
                                  padding: "2px 7px",
                                  borderRadius: 10,
                                  fontSize: "9px",
                                  fontWeight: 700,
                                  textTransform: "uppercase",
                                  letterSpacing: "0.4px",
                                }}
                              >
                                PENDING
                              </div>
                            )}
                            {notification.ticketStatus === "pending" && (
                              <div style={{ display: "flex", gap: 4 }}>
                                <Tooltip title="Aceptar ticket">
                                  <IconButton
                                    size="small"
                                    onClick={(e) => { e.stopPropagation(); acceptTicket(notification); }}
                                    style={{
                                      color: paleta.aceptar,
                                      padding: 5,
                                      width: 26,
                                      height: 26,
                                    }}
                                  >
                                    <CheckIcon style={{ fontSize: "16px" }} />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Finalizar ticket">
                                  <IconButton
                                    size="small"
                                    onClick={(e) => { e.stopPropagation(); finalizeTicket(notification); }}
                                    style={{
                                      color: paleta.finalizar,
                                      padding: 5,
                                      width: 26,
                                      height: 26,
                                    }}
                                  >
                                    <CloseIcon style={{ fontSize: "16px" }} />
                                  </IconButton>
                                </Tooltip>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </ListItem>
                </Tooltip>
              ))}
            </List>
          )}
        </div>
      </Popover>
    </div>
  );
};

export default NotificationsPopOver;
